import moment from 'moment';
import HelperMixin from '../../Shared/HelperMixin.vue';

export default {
    name: 'NeedEstimates',
    mixins: [HelperMixin],

    async created() {
        await this.CheckLoginStatus();
        if (!this.loginDetails || !this.canViewPage) {
            this.$helper.ShowMessage('error', 'غير مصرح', 'واجهة بيانات الاحتياج متاحة لمستخدمي المرافق وإدارة الصيدلة.');
            this.href('/Home');
            return;
        }

        if (this.isMinistry) {
            this.loadBranches();
        } else {
            // مستخدم مرفق: ورقة مرفقه مباشرة
            this.loadSheet();
        }
    },

    computed: {
        /** أي موظف تابع للمرفق (قرار العميل) أو مستخدم وزاري مخوَّل. */
        canViewPage() {
            return !!(this.loginDetails && (this.isFacilityCenterRole() || this.hasInventoryAndStockAccessUi()));
        },
        isMinistry() {
            return this.hasInventoryAndStockAccessUi();
        },
        /** عدد الأصناف التي أُدخل لها احتياج. */
        filledCount() {
            return this.rows.filter((r) => r.monthlyNeed != null && Number(r.monthlyNeed) > 0).length;
        },
        /** أسطر تغيّرت عن قيمتها المحفوظة — هي فقط ما يُرسل عند الحفظ. */
        dirtyRows() {
            return this.rows.filter((r) => {
                const current = r.monthlyNeed == null || r.monthlyNeed === '' ? 0 : Number(r.monthlyNeed);
                const original = r.originalMonthly == null ? 0 : Number(r.originalMonthly);
                return current !== original;
            });
        },
        hasConsumptionData() {
            return this.rows.some((r) => r.avgMonthlyConsumption != null);
        },
    },

    filters: {
        moment(date) {
            if (!date) return '—';
            return moment(date).format('DD/MM/YYYY');
        },
    },

    data() {
        return {
            mode: 'sheet', // sheet = حسب المرفق | product = حسب الصنف (وزارة فقط)

            branches: [],
            selectedBranchId: null,

            search: '',
            rows: [],
            consumptionMonths: 6,
            loadingSheet: false,

            // مقارنة حسب الصنف
            productOptions: [],
            productSearchLoading: false,
            selectedFormularyItemId: null,
            productInfo: null,
            productRows: [],
        };
    },

    methods: {
        // ─── الأسبوعي والسنوي: حساب لحظي أمام المستخدم ───────────────────
        weeklyOf(monthly) {
            const m = Number(monthly);
            if (!Number.isFinite(m) || m <= 0) return null;
            return Math.ceil(m / 4); // التقريب لأعلى — قرار العميل
        },
        annualOf(monthly) {
            const m = Number(monthly);
            if (!Number.isFinite(m) || m <= 0) return null;
            return m * 12;
        },

        apiError(err, fallback) {
            const msg = err && err.response && err.response.data ? err.response.data : fallback;
            this.$helper.ShowMessage('error', 'خطأ', msg);
        },

        // ─── ورقة المرفق ─────────────────────────────────────────────────
        loadBranches() {
            this.$http.GetAllBranches()
                .then((res) => {
                    this.branches = (res && res.data && res.data.info) || [];
                })
                .catch(() => { this.branches = []; });
        },

        onBranchChange() {
            this.rows = [];
            if (this.selectedBranchId) this.loadSheet();
        },

        loadSheet() {
            if (this.isMinistry && !this.selectedBranchId) return;
            this.loadingSheet = true;
            this.$blockUI.Start();
            this.$http.GetNeedEstimatesSheet(this.isMinistry ? this.selectedBranchId : null, this.search || null)
                .then((res) => {
                    this.$blockUI.Stop();
                    this.loadingSheet = false;
                    const d = (res && res.data) || {};
                    this.consumptionMonths = d.consumptionMonths || 6;
                    this.rows = (d.info || []).map((r) => ({
                        nationalFormularyItemsId: r.nationalFormularyItemsId,
                        name: r.name,
                        nationalCode: r.nationalCode,
                        baseUnitName: r.baseUnitName,
                        monthlyNeed: r.monthlyNeed,
                        originalMonthly: r.monthlyNeed,
                        avgMonthlyConsumption: r.avgMonthlyConsumption,
                        countShortage: r.countShortage,
                        modifiedOn: r.modifiedOn,
                    }));
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    this.loadingSheet = false;
                    this.rows = [];
                    this.apiError(err, 'تعذّر تحميل بيانات الاحتياج.');
                });
        },

        /** اعتماد الاستهلاك الفعلي احتياجاً شهرياً لصنف واحد. */
        applyActual(row) {
            if (row.avgMonthlyConsumption == null) return;
            row.monthlyNeed = Number(row.avgMonthlyConsumption);
        },

        /** اعتماد الفعلي لكل الأصناف التي لها استهلاك مسجَّل. */
        applyActualAll() {
            const candidates = this.rows.filter((r) => r.avgMonthlyConsumption != null);
            if (!candidates.length) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'لا توجد بيانات استهلاك فعلي بعد — تظهر عند بدء المرافق بتسجيل صرفها الداخلي.');
                return;
            }
            this.$confirm(
                `سيُعبَّأ الاحتياج الشهري من متوسط الاستهلاك الفعلي لـ ${candidates.length} صنفاً (يبقى الحفظ بيدك). متابعة؟`,
                'تحديث من الاستهلاك الفعلي',
                { confirmButtonText: 'تعبئة', cancelButtonText: 'تراجع', type: 'warning' }
            ).then(() => {
                candidates.forEach((r) => { r.monthlyNeed = Number(r.avgMonthlyConsumption); });
            }).catch(() => { });
        },

        saveAll() {
            const dirty = this.dirtyRows;
            if (!dirty.length) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'لا توجد تغييرات للحفظ.');
                return;
            }

            const bad = dirty.find((r) => r.monthlyNeed != null && r.monthlyNeed !== '' && Number(r.monthlyNeed) < 0);
            if (bad) {
                this.$helper.ShowMessage('warning', 'تنبيه', `قيمة غير صحيحة للصنف «${bad.name}».`);
                return;
            }

            const payload = {
                BranchesId: this.isMinistry ? Number(this.selectedBranchId) : null,
                Lines: dirty.map((r) => ({
                    NationalFormularyItemsId: r.nationalFormularyItemsId,
                    MonthlyNeed: r.monthlyNeed == null || r.monthlyNeed === '' ? 0 : Number(r.monthlyNeed),
                })),
            };

            this.$blockUI.Start();
            this.$http.SaveNeedEstimates(payload)
                .then((res) => {
                    this.$blockUI.Stop();
                    this.$helper.ShowMessage('success', 'تم', (res.data && res.data.message) || 'تم حفظ بيانات الاحتياج.');
                    this.loadSheet();
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    this.apiError(err, 'تعذّر حفظ بيانات الاحتياج.');
                });
        },

        // ─── المقارنة حسب الصنف (وزارة) ──────────────────────────────────
        searchProducts(query) {
            this.productSearchLoading = true;
            this.$http.SearchNeedFormularyItems(query || null)
                .then((res) => {
                    this.productSearchLoading = false;
                    this.productOptions = (res && res.data && res.data.info) || [];
                })
                .catch(() => {
                    this.productSearchLoading = false;
                    this.productOptions = [];
                });
        },

        loadByProduct() {
            if (!this.selectedFormularyItemId) return;
            this.$blockUI.Start();
            this.$http.GetNeedEstimatesByProduct(this.selectedFormularyItemId)
                .then((res) => {
                    this.$blockUI.Stop();
                    const d = (res && res.data) || {};
                    this.productInfo = d.item || null;
                    this.productRows = d.info || [];
                })
                .catch((err) => {
                    this.$blockUI.Stop();
                    this.productInfo = null;
                    this.productRows = [];
                    this.apiError(err, 'تعذّر تحميل المقارنة.');
                });
        },

        switchMode(m) {
            this.mode = m;
            if (m === 'product' && !this.productOptions.length) this.searchProducts('');
        },
    },
};
